/**
 * Géométrie des icônes — la SOURCE, agnostique de toute plateforme.
 *
 * Un tracé d'icône est une donnée, pas un composant : les mêmes coordonnées
 * décrivent le dessin qu'il finisse dans un <svg> du navigateur ou dans un
 * <Svg> de react-native-svg. Les garder ici plutôt que dans le JSX de chaque
 * cible est ce qui empêche les deux jeux de DÉRIVER — un tracé corrigé sur le
 * web mais pas sur le natif, et le système montre deux dessins pour une même
 * icône.
 *
 * C'est le rôle que @one-nexty/tokens tient déjà pour les couleurs, et les deux
 * packages d'icônes en sont les ADAPTATEURS :
 *
 *   geometry.ts ──▶ @one-nexty/icons          (<svg>, currentColor, Tailwind)
 *               └─▶ @one-nexty/icons-native   (<Svg>, couleur explicite, RN)
 *
 * Les noms d'attributs sont ceux de la spécification SVG, et react-native-svg
 * les reprend tels quels : aucune traduction n'est nécessaire entre les cibles.
 */

/** Les seules primitives SVG que le jeu d'icônes utilise. */
export type IconTag = "path" | "circle" | "ellipse" | "rect" | "line" | "polyline" | "polygon";

/**
 * Une primitive du dessin.
 *
 * Toutes les coordonnées sont des chaînes et non des nombres : c'est ainsi
 * qu'elles s'écrivent dans un fichier SVG, les deux moteurs de rendu les
 * acceptent, et cela évite qu'un « 3.01 » devienne « 3.0099999 » au passage.
 *
 * `stroke` et `fill` ne sont présents que là où la primitive S'ÉCARTE du réglage
 * de l'icône — un `stroke: "none"` sur la forme pleine d'une icône solide, par
 * exemple. Absents, les réglages de l'icône s'appliquent.
 */
export interface IconShape {
  readonly tag: IconTag;
  readonly d?: string;
  readonly cx?: string;
  readonly cy?: string;
  readonly r?: string;
  readonly x?: string;
  readonly y?: string;
  readonly width?: string;
  readonly height?: string;
  readonly rx?: string;
  readonly ry?: string;
  readonly x1?: string;
  readonly y1?: string;
  readonly x2?: string;
  readonly y2?: string;
  readonly points?: string;
  readonly stroke?: string;
  readonly fill?: string;
  readonly strokeWidth?: string;
}

export interface IconGeometry {
  /**
   * Icône PLEINE : le dessin est rempli de la couleur du texte au lieu d'être
   * seulement tracé. Les paires outline/solid du système (`StarIcon` /
   * `StarSolidIcon`) ne diffèrent que par ce drapeau et quelques `stroke: "none"`.
   */
  readonly solid: boolean;
  readonly shapes: readonly IconShape[];
}

/** Grille de dessin. Toute icône du système s'inscrit dans ce carré. */
export const ICON_VIEWBOX = "0 0 24 24";

/** Épaisseur de trait par défaut, choisie pour rester lisible dès 16 px. */
export const ICON_STROKE_WIDTH = 1.75;

/* ==========================================================================
 * Une constante par icône, et non un seul grand objet.
 *
 * C'est ce qui garde le jeu ÉLAGABLE : un bundler sait supprimer une constante
 * exportée que personne n'importe, mais pas une entrée au milieu d'un objet.
 * Regroupées, toutes les icônes voyageraient dans le bundle d'une application
 * qui n'en affiche que trois.
 *
 * L'agrégat `iconGeometry` en fin de fichier reste disponible pour les galeries,
 * qui les veulent justement toutes — l'importer, c'est les embarquer toutes,
 * et c'est alors le comportement voulu.
 * ======================================================================== */

/* -------------------------------------------------------------------------- */
/* 1. ACTIONS & NAVIGATION                                                  */
/* -------------------------------------------------------------------------- */

export const PlusIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "M12 5v14M5 12h14" },
  ],
};

export const MinusIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "M5 12h14" },
  ],
};

export const XIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "M18 6L6 18M6 6l12 12" },
  ],
};

export const CheckIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "M20 6L9 17l-5-5" },
  ],
};

export const SearchIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "circle", cx: "11", cy: "11", r: "7" },
    { tag: "path", d: "M20 20l-3.5-3.5" },
  ],
};

export const ArrowRightIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "M5 12h14M13 6l6 6-6 6" },
  ],
};

export const ArrowLeftIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "M19 12H5M11 18l-6-6 6-6" },
  ],
};

export const ArrowUpIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "M12 19V5M5 12l7-7 7 7" },
  ],
};

export const ArrowDownIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "M12 5v14M19 12l-7 7-7-7" },
  ],
};

export const ChevronDownIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "M6 9l6 6 6-6" },
  ],
};

export const ChevronUpIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "M18 15l-6-6-6 6" },
  ],
};

export const ChevronRightIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "M9 18l6-6-6-6" },
  ],
};

export const ChevronLeftIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "M15 6l-6 6 6 6" },
  ],
};

export const RefreshIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "M21 12a9 9 0 0 0-9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" },
    { tag: "path", d: "M3 3v5h5" },
    { tag: "path", d: "M3 12a9 9 0 0 0 9 9 9.75 9.75 0 0 0 6.74-2.74L21 16" },
    { tag: "path", d: "M16 16h5v5" },
  ],
};

export const ExternalLinkIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" },
    { tag: "polyline", points: "15 3 21 3 21 9" },
    { tag: "line", x1: "10", y1: "14", x2: "21", y2: "3" },
  ],
};

export const ShareIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "circle", cx: "18", cy: "5", r: "3" },
    { tag: "circle", cx: "6", cy: "12", r: "3" },
    { tag: "circle", cx: "18", cy: "19", r: "3" },
    { tag: "line", x1: "8.59", y1: "13.51", x2: "15.42", y2: "17.49" },
    { tag: "line", x1: "15.41", y1: "6.51", x2: "8.59", y2: "10.49" },
  ],
};

export const FilterIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "polygon", points: "22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" },
  ],
};

export const GridIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "rect", x: "3", y: "3", width: "7", height: "7", rx: "1" },
    { tag: "rect", x: "14", y: "3", width: "7", height: "7", rx: "1" },
    { tag: "rect", x: "14", y: "14", width: "7", height: "7", rx: "1" },
    { tag: "rect", x: "3", y: "14", width: "7", height: "7", rx: "1" },
  ],
};

/**
 * Trois traits pleine largeur — le bouton de menu.
 *
 * Distincte de `ListIcon`, qui porte des puces et commence à x=8 : celle-ci
 * désigne une ACTION (ouvrir la navigation), l'autre un CONTENU (une liste).
 * Les confondre est courant et donne un bouton de menu qui a l'air d'un filtre.
 */
export const MenuIconGeometry: IconGeometry = {
  solid: false,
  shapes: [{ tag: "path", d: "M4 7h16M4 12h16M4 17h16" }],
};

/**
 * Trois points — « d'autres actions ».
 *
 * Les points sont PLEINS et sans contour : un cercle tracé de 1,75 d'épaisseur
 * sur un rayon de 1 se referme sur lui-même et donne une tache irrégulière.
 * C'est la seule primitive du jeu où le remplissage ne suit pas le drapeau
 * `solid`, d'où le `fill` porté par la forme elle-même.
 */
export const MoreVerticalIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "circle", cx: "12", cy: "5", r: "1.5", stroke: "none", fill: "currentColor" },
    { tag: "circle", cx: "12", cy: "12", r: "1.5", stroke: "none", fill: "currentColor" },
    { tag: "circle", cx: "12", cy: "19", r: "1.5", stroke: "none", fill: "currentColor" },
  ],
};

export const MoreHorizontalIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "circle", cx: "5", cy: "12", r: "1.5", stroke: "none", fill: "currentColor" },
    { tag: "circle", cx: "12", cy: "12", r: "1.5", stroke: "none", fill: "currentColor" },
    { tag: "circle", cx: "19", cy: "12", r: "1.5", stroke: "none", fill: "currentColor" },
  ],
};

export const ListIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "line", x1: "8", y1: "6", x2: "21", y2: "6" },
    { tag: "line", x1: "8", y1: "12", x2: "21", y2: "12" },
    { tag: "line", x1: "8", y1: "18", x2: "21", y2: "18" },
    { tag: "line", x1: "3", y1: "6", x2: "3.01", y2: "6" },
    { tag: "line", x1: "3", y1: "12", x2: "3.01", y2: "12" },
    { tag: "line", x1: "3", y1: "18", x2: "3.01", y2: "18" },
  ],
};

/* -------------------------------------------------------------------------- */
/* 2. THÈME, FAVORIS & ÉTATS ACTIFS (OUTLINE & SOLID PAIRS)                 */
/* -------------------------------------------------------------------------- */

/* --- Soleil --- */

export const SunIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "circle", cx: "12", cy: "12", r: "4" },
    { tag: "path", d: "M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" },
  ],
};

export const SunSolidIconGeometry: IconGeometry = {
  solid: true,
  shapes: [
    { tag: "circle", cx: "12", cy: "12", r: "5", stroke: "none" },
    { tag: "path", d: "M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41", stroke: "currentColor" },
  ],
};

/* --- Lune --- */

export const MoonIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z" },
  ],
};

export const MoonSolidIconGeometry: IconGeometry = {
  solid: true,
  shapes: [
    { tag: "path", d: "M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5Z", stroke: "none" },
  ],
};

/* --- Cœur --- */

export const HeartIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" },
  ],
};

export const HeartSolidIconGeometry: IconGeometry = {
  solid: true,
  shapes: [
    { tag: "path", d: "M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z", stroke: "none" },
  ],
};

/* --- Étoile --- */

export const StarIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "polygon", points: "12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" },
  ],
};

export const StarSolidIconGeometry: IconGeometry = {
  solid: true,
  shapes: [
    { tag: "polygon", points: "12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2", stroke: "none" },
  ],
};

/* --- Marque-page --- */

export const BookmarkIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" },
  ],
};

export const BookmarkSolidIconGeometry: IconGeometry = {
  solid: true,
  shapes: [
    { tag: "path", d: "M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z", stroke: "none" },
  ],
};

/* --- Cloche --- */

export const BellIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" },
    { tag: "path", d: "M13.73 21a2 2 0 0 1-3.46 0" },
  ],
};

export const BellSolidIconGeometry: IconGeometry = {
  solid: true,
  shapes: [
    { tag: "path", d: "M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9", stroke: "none" },
    { tag: "path", d: "M13.73 21a2 2 0 0 1-3.46 0", stroke: "currentColor", fill: "none" },
  ],
};

/* --- Cadenas --- */

export const LockIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "rect", x: "3", y: "11", width: "18", height: "11", rx: "2", ry: "2" },
    { tag: "path", d: "M7 11V7a5 5 0 0 1 10 0v4" },
  ],
};

export const LockOpenIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "rect", x: "3", y: "11", width: "18", height: "11", rx: "2", ry: "2" },
    { tag: "path", d: "M7 11V7a5 5 0 0 1 9.9-1" },
  ],
};

export const LockSolidIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "rect", x: "3", y: "11", width: "18", height: "11", rx: "2", ry: "2", stroke: "none", fill: "currentColor" },
    { tag: "path", d: "M7 11V7a5 5 0 0 1 10 0v4", stroke: "currentColor", fill: "none" },
  ],
};

/* --- Bouclier --- */

export const ShieldIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" },
  ],
};

export const ShieldSolidIconGeometry: IconGeometry = {
  solid: true,
  shapes: [
    { tag: "path", d: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z", stroke: "none" },
  ],
};

/* --- Dossier --- */

export const FolderIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" },
  ],
};

export const FolderSolidIconGeometry: IconGeometry = {
  solid: true,
  shapes: [
    { tag: "path", d: "M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z", stroke: "none" },
  ],
};

/* -------------------------------------------------------------------------- */
/* 3. SÉLECTION & CHECKBOX / RADIO / VUES                                   */
/* -------------------------------------------------------------------------- */

export const SquareIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "rect", x: "3", y: "3", width: "18", height: "18", rx: "2", ry: "2" },
  ],
};

export const CheckSquareIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "polyline", points: "9 11 12 14 22 4" },
    { tag: "path", d: "M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" },
  ],
};

export const CheckSquareSolidIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "rect", x: "3", y: "3", width: "18", height: "18", rx: "2", ry: "2", stroke: "none", fill: "currentColor" },
    { tag: "path", d: "m9 12 2.5 2.5L16.5 9", stroke: "var(--bg-surface, #ffffff)", fill: "none", strokeWidth: "2.5" },
  ],
};

export const CircleIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "circle", cx: "12", cy: "12", r: "9" },
  ],
};

export const CircleDotIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "circle", cx: "12", cy: "12", r: "9" },
    { tag: "circle", cx: "12", cy: "12", r: "3", fill: "currentColor" },
  ],
};

/* -------------------------------------------------------------------------- */
/* 4. SYSTÈME, TECHNIQUE & MÉDIAS                                           */
/* -------------------------------------------------------------------------- */

export const UserIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "circle", cx: "12", cy: "8", r: "4" },
    { tag: "path", d: "M5 20a7 7 0 0 1 14 0" },
  ],
};

export const UserSolidIconGeometry: IconGeometry = {
  solid: true,
  shapes: [
    { tag: "circle", cx: "12", cy: "8", r: "4", stroke: "none" },
    { tag: "path", d: "M5 20a7 7 0 0 1 14 0", stroke: "none" },
  ],
};

export const UsersIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "circle", cx: "9", cy: "8", r: "3.5" },
    { tag: "path", d: "M2.5 20a6.5 6.5 0 0 1 13 0M17 11a3 3 0 1 0 0-6M18 20a6 6 0 0 0-2-4.5" },
  ],
};

export const UsersSolidIconGeometry: IconGeometry = {
  solid: true,
  shapes: [
    { tag: "circle", cx: "9", cy: "8", r: "3.5", stroke: "none" },
    { tag: "path", d: "M2.5 20a6.5 6.5 0 0 1 13 0M17 11a3 3 0 1 0 0-6M18 20a6 6 0 0 0-2-4.5", stroke: "none" },
  ],
};

export const CopyIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "rect", x: "9", y: "9", width: "11", height: "11", rx: "2" },
    { tag: "path", d: "M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" },
  ],
};

export const FileIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" },
    { tag: "path", d: "M14 3v5h5" },
  ],
};

export const TrashIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "M4 7h16M10 11v6M14 11v6M5 7l1 13a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2l1-13M9 7V4h6v3" },
  ],
};

export const InboxIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "M3 12h5l1.5 3h5L16 12h5" },
    { tag: "path", d: "M4.5 6.5L3 12v6a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-6l-1.5-5.5A2 2 0 0 0 17.6 5H6.4a2 2 0 0 0-1.9 1.5Z" },
  ],
};

export const UploadIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5" },
    { tag: "path", d: "M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" },
  ],
};

export const DownloadIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "M12 4v12m0 0l-4.5-4.5M12 16l4.5-4.5" },
    { tag: "path", d: "M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" },
  ],
};

export const SettingsIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "circle", cx: "12", cy: "12", r: "3" },
    { tag: "path", d: "M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1Z" },
  ],
};

export const SparklesIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "m12 3 2.5 5.5L20 11l-5.5 2.5L12 19l-2.5-5.5L4 11l5.5-2.5Z" },
    { tag: "path", d: "M5 3l1 2 2 1-2 1-1 2-1-2-2-1 2-1Z" },
  ],
};

export const PlayIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "polygon", points: "5 3 19 12 5 21 5 3" },
  ],
};

export const PauseIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "rect", x: "6", y: "4", width: "4", height: "16", rx: "1" },
    { tag: "rect", x: "14", y: "4", width: "4", height: "16", rx: "1" },
  ],
};

export const EyeIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12Z" },
    { tag: "circle", cx: "12", cy: "12", r: "2.75" },
  ],
};

export const EyeOffIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "M10.6 6.1A9.9 9.9 0 0 1 12 6c6.4 0 10 6 10 6a17 17 0 0 1-3 3.6M6.3 7.7C3.6 9.4 2 12 2 12s3.6 6.5 10 6.5c1.6 0 3-.3 4.3-.8" },
    { tag: "path", d: "M9.9 9.9a3 3 0 0 0 4.2 4.2M3 3l18 18" },
  ],
};

export const VolumeIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "polygon", points: "11 5 6 9 2 9 2 15 6 15 11 19 11 5" },
    { tag: "path", d: "M15.54 8.46a5 5 0 0 1 0 7.07" },
    { tag: "path", d: "M19.07 4.93a10 10 0 0 1 0 14.14" },
  ],
};

export const VolumeMuteIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "polygon", points: "11 5 6 9 2 9 2 15 6 15 11 19 11 5" },
    { tag: "line", x1: "23", y1: "9", x2: "17", y2: "15" },
    { tag: "line", x1: "17", y1: "9", x2: "23", y2: "15" },
  ],
};

export const ChartIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "M4 20V10M10 20V4M16 20v-7M22 20H2" },
  ],
};

export const CloudIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" },
  ],
};

export const ServerIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "rect", x: "2", y: "2", width: "20", height: "8", rx: "2", ry: "2" },
    { tag: "rect", x: "2", y: "14", width: "20", height: "8", rx: "2", ry: "2" },
    { tag: "line", x1: "6", y1: "6", x2: "6.01", y2: "6" },
    { tag: "line", x1: "6", y1: "18", x2: "6.01", y2: "18" },
  ],
};

export const DatabaseIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "ellipse", cx: "12", cy: "5", rx: "9", ry: "3" },
    { tag: "path", d: "M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" },
    { tag: "path", d: "M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" },
  ],
};

export const CpuIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "rect", x: "4", y: "4", width: "16", height: "16", rx: "2" },
    { tag: "rect", x: "9", y: "9", width: "6", height: "6" },
    { tag: "line", x1: "9", y1: "1", x2: "9", y2: "4" },
    { tag: "line", x1: "15", y1: "1", x2: "15", y2: "4" },
    { tag: "line", x1: "9", y1: "20", x2: "9", y2: "23" },
    { tag: "line", x1: "15", y1: "20", x2: "15", y2: "23" },
    { tag: "line", x1: "20", y1: "9", x2: "23", y2: "9" },
    { tag: "line", x1: "20", y1: "15", x2: "23", y2: "15" },
    { tag: "line", x1: "1", y1: "9", x2: "4", y2: "9" },
    { tag: "line", x1: "1", y1: "15", x2: "4", y2: "15" },
  ],
};

export const LayersIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "polygon", points: "12 2 2 7 12 12 22 7 12 2" },
    { tag: "polyline", points: "2 17 12 22 22 17" },
    { tag: "polyline", points: "2 12 12 17 22 12" },
  ],
};

export const GlobeIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "circle", cx: "12", cy: "12", r: "10" },
    { tag: "line", x1: "2", y1: "12", x2: "22", y2: "12" },
    { tag: "path", d: "M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" },
  ],
};

export const MailIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" },
    { tag: "polyline", points: "22,6 12,13 2,6" },
  ],
};

export const PhoneIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" },
  ],
};

export const MapPinIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" },
    { tag: "circle", cx: "12", cy: "10", r: "3" },
  ],
};

export const ClockIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "circle", cx: "12", cy: "12", r: "10" },
    { tag: "polyline", points: "12 6 12 12 16 14" },
  ],
};

export const CalendarIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "rect", x: "3", y: "4", width: "18", height: "18", rx: "2", ry: "2" },
    { tag: "line", x1: "16", y1: "2", x2: "16", y2: "6" },
    { tag: "line", x1: "8", y1: "2", x2: "8", y2: "6" },
    { tag: "line", x1: "3", y1: "10", x2: "21", y2: "10" },
  ],
};

export const ZapIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "polygon", points: "13 2 3 14 12 14 11 22 21 10 12 10 13 2" },
  ],
};

export const CodeIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "polyline", points: "16 18 22 12 16 6" },
    { tag: "polyline", points: "8 6 2 12 8 18" },
  ],
};

export const TerminalIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "polyline", points: "4 17 10 11 4 5" },
    { tag: "line", x1: "12", y1: "19", x2: "20", y2: "19" },
  ],
};

/* --- Status & Notifications --- */

export const InfoIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "circle", cx: "12", cy: "12", r: "10" },
    { tag: "path", d: "M12 16v-4M12 8h.01" },
  ],
};

export const InfoSolidIconGeometry: IconGeometry = {
  solid: true,
  shapes: [
    { tag: "circle", cx: "12", cy: "12", r: "10", stroke: "none" },
    { tag: "path", d: "M12 16v-4M12 8h.01", stroke: "var(--bg-surface, #ffffff)", fill: "none", strokeWidth: "2.5" },
  ],
};

export const AlertTriangleIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "path", d: "m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" },
    { tag: "path", d: "M12 9v4M12 17h.01" },
  ],
};

export const AlertTriangleSolidIconGeometry: IconGeometry = {
  solid: true,
  shapes: [
    { tag: "path", d: "m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z", stroke: "none" },
    { tag: "path", d: "M12 9v4M12 17h.01", stroke: "var(--bg-surface, #ffffff)", fill: "none", strokeWidth: "2.5" },
  ],
};

export const CheckCircleIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "circle", cx: "12", cy: "12", r: "10" },
    { tag: "path", d: "m9 12 2 2 4-4" },
  ],
};

export const CheckCircleSolidIconGeometry: IconGeometry = {
  solid: true,
  shapes: [
    { tag: "circle", cx: "12", cy: "12", r: "10", stroke: "none" },
    { tag: "path", d: "m9 12 2 2 4-4", stroke: "var(--bg-surface, #ffffff)", fill: "none", strokeWidth: "2.5" },
  ],
};

export const XCircleIconGeometry: IconGeometry = {
  solid: false,
  shapes: [
    { tag: "circle", cx: "12", cy: "12", r: "10" },
    { tag: "path", d: "m15 9-6 6M9 9l6 6" },
  ],
};

export const XCircleSolidIconGeometry: IconGeometry = {
  solid: true,
  shapes: [
    { tag: "circle", cx: "12", cy: "12", r: "10", stroke: "none" },
    { tag: "path", d: "m15 9-6 6M9 9l6 6", stroke: "var(--bg-surface, #ffffff)", fill: "none", strokeWidth: "2.5" },
  ],
};

/* -------------------------------------------------------------------------- */
/* Agrégat                                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Le jeu complet, indexé par nom.
 *
 * Réservé à ce qui a BESOIN de tout : une galerie, un test de non-régression
 * qui parcourt les 84 tracés. Un composant, lui, importe la constante de son
 * icône — voir la note en tête de fichier.
 */
export const iconGeometry = {
  PlusIcon: PlusIconGeometry,
  MinusIcon: MinusIconGeometry,
  XIcon: XIconGeometry,
  CheckIcon: CheckIconGeometry,
  SearchIcon: SearchIconGeometry,
  ArrowRightIcon: ArrowRightIconGeometry,
  ArrowLeftIcon: ArrowLeftIconGeometry,
  ArrowUpIcon: ArrowUpIconGeometry,
  ArrowDownIcon: ArrowDownIconGeometry,
  ChevronDownIcon: ChevronDownIconGeometry,
  ChevronUpIcon: ChevronUpIconGeometry,
  ChevronRightIcon: ChevronRightIconGeometry,
  ChevronLeftIcon: ChevronLeftIconGeometry,
  RefreshIcon: RefreshIconGeometry,
  ExternalLinkIcon: ExternalLinkIconGeometry,
  ShareIcon: ShareIconGeometry,
  FilterIcon: FilterIconGeometry,
  GridIcon: GridIconGeometry,
  MenuIcon: MenuIconGeometry,
  MoreVerticalIcon: MoreVerticalIconGeometry,
  MoreHorizontalIcon: MoreHorizontalIconGeometry,
  ListIcon: ListIconGeometry,
  SunIcon: SunIconGeometry,
  SunSolidIcon: SunSolidIconGeometry,
  MoonIcon: MoonIconGeometry,
  MoonSolidIcon: MoonSolidIconGeometry,
  HeartIcon: HeartIconGeometry,
  HeartSolidIcon: HeartSolidIconGeometry,
  StarIcon: StarIconGeometry,
  StarSolidIcon: StarSolidIconGeometry,
  BookmarkIcon: BookmarkIconGeometry,
  BookmarkSolidIcon: BookmarkSolidIconGeometry,
  BellIcon: BellIconGeometry,
  BellSolidIcon: BellSolidIconGeometry,
  LockIcon: LockIconGeometry,
  LockOpenIcon: LockOpenIconGeometry,
  LockSolidIcon: LockSolidIconGeometry,
  ShieldIcon: ShieldIconGeometry,
  ShieldSolidIcon: ShieldSolidIconGeometry,
  FolderIcon: FolderIconGeometry,
  FolderSolidIcon: FolderSolidIconGeometry,
  SquareIcon: SquareIconGeometry,
  CheckSquareIcon: CheckSquareIconGeometry,
  CheckSquareSolidIcon: CheckSquareSolidIconGeometry,
  CircleIcon: CircleIconGeometry,
  CircleDotIcon: CircleDotIconGeometry,
  UserIcon: UserIconGeometry,
  UserSolidIcon: UserSolidIconGeometry,
  UsersIcon: UsersIconGeometry,
  UsersSolidIcon: UsersSolidIconGeometry,
  CopyIcon: CopyIconGeometry,
  FileIcon: FileIconGeometry,
  TrashIcon: TrashIconGeometry,
  InboxIcon: InboxIconGeometry,
  UploadIcon: UploadIconGeometry,
  DownloadIcon: DownloadIconGeometry,
  SettingsIcon: SettingsIconGeometry,
  SparklesIcon: SparklesIconGeometry,
  PlayIcon: PlayIconGeometry,
  PauseIcon: PauseIconGeometry,
  EyeIcon: EyeIconGeometry,
  EyeOffIcon: EyeOffIconGeometry,
  VolumeIcon: VolumeIconGeometry,
  VolumeMuteIcon: VolumeMuteIconGeometry,
  ChartIcon: ChartIconGeometry,
  CloudIcon: CloudIconGeometry,
  ServerIcon: ServerIconGeometry,
  DatabaseIcon: DatabaseIconGeometry,
  CpuIcon: CpuIconGeometry,
  LayersIcon: LayersIconGeometry,
  GlobeIcon: GlobeIconGeometry,
  MailIcon: MailIconGeometry,
  PhoneIcon: PhoneIconGeometry,
  MapPinIcon: MapPinIconGeometry,
  ClockIcon: ClockIconGeometry,
  CalendarIcon: CalendarIconGeometry,
  ZapIcon: ZapIconGeometry,
  CodeIcon: CodeIconGeometry,
  TerminalIcon: TerminalIconGeometry,
  InfoIcon: InfoIconGeometry,
  InfoSolidIcon: InfoSolidIconGeometry,
  AlertTriangleIcon: AlertTriangleIconGeometry,
  AlertTriangleSolidIcon: AlertTriangleSolidIconGeometry,
  CheckCircleIcon: CheckCircleIconGeometry,
  CheckCircleSolidIcon: CheckCircleSolidIconGeometry,
  XCircleIcon: XCircleIconGeometry,
  XCircleSolidIcon: XCircleSolidIconGeometry,
} as const satisfies Record<string, IconGeometry>;

/** Nom de toute icône du jeu statique. Sert de contrat commun aux deux cibles. */
export type IconName = keyof typeof iconGeometry;

/** Les noms, dans l'ordre du fichier. Utile aux vitrines et aux tests. */
export const ICON_NAMES = Object.keys(iconGeometry) as readonly IconName[];
